import {
  Box,
  CollateralHealthStatusEnum,
  Flex,
  Grid,
  HStack,
  Icon,
  ICON_SIZES,
  IconName,
  Popover,
  Text,
  usePopoverState,
} from '@talos/kyoko';
import type { ICellRendererParams } from 'ag-grid-enterprise';
import type { ReactNode } from 'react';
import styled, { useTheme } from 'styled-components';
import { CollateralHealthMarkerTriangle } from './CollateralHealthMarkerTriangle';
import { CollateralStatusTooltip } from './CollateralStatusTooltip';
import {
  canRenderCollateralHealthBar,
  COLLATERAL_HEALTH_ZONE_COUNT,
  formatShortfallPctOfMta,
  getCollateralHealthMarkerColor,
  getCollateralHealthMarkerPlacement,
  getCollateralHealthMarkerRole,
  getCollateralHealthStatusDisplayName,
  getCollateralHealthZoneColors,
  getFormattedCollateralRatio,
  getPrimaryCollateralShortfallView,
  getShortfallAccentColor,
  getStatusColor,
  hasDefinedMinTransferAmount,
  type CollateralHealthMarkerPlacement,
  type CollateralHealthMarkerRole,
} from './collateralStatusUtils';
import {
  hasCollateralStatusDetails,
  isCounterpartyMonitoringCollateralizedDeal,
  type CounterpartyMonitoringCollateralizedDealWithStatusDetails,
  type CounterpartyMonitoringRow,
} from './counterpartyMonitoringRows';

const ZONE_IDS = ['liquidation', 'margin-call', 'healthy', 'release'] as const;

const CellWrapper = styled(Box)`
  box-sizing: border-box;
  display: flex;
  width: 100%;
  height: 100%;
  min-width: 100%;
  flex-direction: column;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing8}px;
  padding: ${({ theme }) => theme.spacing12}px ${({ theme }) => theme.spacing8}px ${({ theme }) => theme.spacing4}px;
`;

const StatusBar = styled(Box)`
  position: relative;
  width: 100%;
  height: 8px;
`;

const ZoneTrack = styled(Box)`
  display: grid;
  grid-template-columns: repeat(${COLLATERAL_HEALTH_ZONE_COUNT}, minmax(0, 1fr));
  gap: 2px;
  width: 100%;
  height: 100%;
  border-radius: 999px;
  overflow: hidden;
`;

const Zone = styled(Box)<{ $background: string }>`
  height: 100%;
  background: ${({ $background }) => $background};
`;

const MarkerOverlay = styled(Box)`
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: repeat(${COLLATERAL_HEALTH_ZONE_COUNT}, minmax(0, 1fr));
  gap: 2px;
  pointer-events: none;
`;

const MarkerSlot = styled(Box)`
  position: relative;
`;

const MarkerPosition = styled(Box)<{ $left: string; $placement: CollateralHealthMarkerRole }>`
  position: absolute;
  left: ${({ $left }) => $left};
  width: fit-content;
  ${({ $placement, theme }) =>
    $placement === 'above' ? `bottom: calc(100% + ${theme.spacing2}px);` : `top: calc(100% + ${theme.spacing2}px);`}
  transform: translateX(-50%);
`;

const AccentText = styled(Text)<{ $color: string }>`
  color: ${({ $color }) => $color};
`;

export const COLLATERAL_STATUS_POPOVER_DELAY_MS = 500;

type StatusMarker = CollateralHealthMarkerPlacement & {
  key: string;
  color: string;
  placement: CollateralHealthMarkerRole;
  status: CollateralHealthStatusEnum | undefined;
};

function CollateralStatusPopover({
  children,
  row,
}: {
  children: ReactNode;
  row: CounterpartyMonitoringCollateralizedDealWithStatusDetails;
}) {
  const popover = usePopoverState({
    trigger: 'hover',
    usePortal: true,
    noPaddingAndBorder: true,
    placement: 'right',
    delay: COLLATERAL_STATUS_POPOVER_DELAY_MS,
    closeOnClickOutside: true,
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['left'],
        },
      },
      {
        name: 'maxHeight',
        enabled: false,
      },
      {
        name: 'preventOverflow',
        enabled: true,
      },
    ],
  });

  return (
    <Popover {...popover} targetStyle={{ width: '100%', height: '100%' }}>
      {children}
      <CollateralStatusTooltip row={row} close={popover.close} />
    </Popover>
  );
}

function StatusLabel({ statusColor, statusDisplayName }: { statusColor: string; statusDisplayName: string }) {
  return (
    <Flex gap="spacing4" minWidth="0" alignItems="center" justifyContent="flex-start">
      <Icon icon={IconName.DotSolid} size={ICON_SIZES.MEDIUM} color={statusColor} data-testid="collateral-status-dot" />
      <Text color="colorTextImportant" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" minWidth="0">
        {statusDisplayName}
      </Text>
    </Flex>
  );
}

export function CollateralStatusCell({ data }: ICellRendererParams<CounterpartyMonitoringRow>) {
  const theme = useTheme();

  if (!isCounterpartyMonitoringCollateralizedDeal(data)) {
    return null;
  }

  const status = data.collateralStatus;

  if (!status) {
    return null;
  }

  if (status === CollateralHealthStatusEnum.Unsecured) {
    const unsecuredLabel = getCollateralHealthStatusDisplayName(status);
    const unsecuredContent = (
      <CellWrapper>
        <Text
          color="gray.090"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          data-testid="collateral-status-unsecured"
        >
          {unsecuredLabel}
        </Text>
      </CellWrapper>
    );

    if (!hasCollateralStatusDetails(data)) {
      return unsecuredContent;
    }

    return <CollateralStatusPopover row={data}>{unsecuredContent}</CollateralStatusPopover>;
  }

  const ratioDisplay = getFormattedCollateralRatio(data);
  const showBar = canRenderCollateralHealthBar(data);
  const primaryPlacement = getCollateralHealthMarkerPlacement(data);
  const activeZoneIndex = primaryPlacement?.zoneIndex;
  const zoneColors = getCollateralHealthZoneColors(activeZoneIndex, theme);
  const statusDisplayName = getCollateralHealthStatusDisplayName(status);
  const statusColor = getStatusColor(status, theme);
  const shortfallView = getPrimaryCollateralShortfallView(data);
  const hasMta = hasDefinedMinTransferAmount(shortfallView?.minTransferAmount);
  const mtaPctLabel = hasMta ? formatShortfallPctOfMta(shortfallView?.shortfallPctMTA) : undefined;
  const mtaCalloutColor = hasMta
    ? getShortfallAccentColor({
        shortfallPctMTA: shortfallView?.shortfallPctMTA,
        status: shortfallView?.status,
        theme,
      })
    : undefined;
  const showMtaCallout = mtaPctLabel != null && mtaCalloutColor != null;

  const markers: StatusMarker[] = (data.collateralStatusDetails?.PriceResults ?? []).flatMap((priceResult, index) => {
    const placement = getCollateralHealthMarkerPlacement(data, priceResult.Ratio);
    if (!placement) {
      return [];
    }

    const role = getCollateralHealthMarkerRole(index);
    const isPrimary = role === 'above';
    return [
      {
        ...placement,
        key: `${priceResult.PriceLabel || 'price-result'}-${index}`,
        color: getCollateralHealthMarkerColor({
          isPrimary,
          status: priceResult.HealthStatus,
          zoneIndex: placement.zoneIndex,
          theme,
        }),
        placement: role,
        status: priceResult.HealthStatus,
      },
    ];
  });

  const cellContent = (
    <CellWrapper>
      {showBar && (
        <StatusBar data-testid="collateral-status-bar">
          <ZoneTrack>
            {ZONE_IDS.map((zoneId, index) => (
              <Zone
                key={zoneId}
                data-testid={`collateral-status-zone-${zoneId}`}
                data-part-id={zoneId}
                data-zone-index={index}
                $background={zoneColors[index]}
              />
            ))}
          </ZoneTrack>
          <MarkerOverlay>
            {ZONE_IDS.map((zoneId, zoneIndex) => (
              <MarkerSlot key={zoneId} data-zone-index={zoneIndex}>
                {markers
                  .filter(marker => marker.zoneIndex === zoneIndex)
                  .map(marker => (
                    <MarkerPosition
                      key={marker.key}
                      $left={`${marker.withinZoneRatio * 100}%`}
                      $placement={marker.placement}
                    >
                      <CollateralHealthMarkerTriangle
                        color={marker.color}
                        placement={marker.placement}
                        data-testid={`collateral-status-marker-${marker.placement}`}
                        data-part-id={`marker-${marker.placement}`}
                        data-zone-index={marker.zoneIndex}
                        data-within-zone-ratio={marker.withinZoneRatio}
                        aria-label={marker.status}
                      />
                    </MarkerPosition>
                  ))}
              </MarkerSlot>
            ))}
          </MarkerOverlay>
        </StatusBar>
      )}
      {showMtaCallout ? (
        <Grid w="100%" gridTemplateColumns="1fr auto 1fr" alignItems="center" gap="spacing12">
          <StatusLabel statusColor={statusColor} statusDisplayName={statusDisplayName} />
          <Text whiteSpace="nowrap" data-testid="collateral-status-mta-callout">
            <AccentText as="span" $color={mtaCalloutColor}>
              {mtaPctLabel}
            </AccentText>
            <Text as="span" color="gray.090">
              {' MTA'}
            </Text>
          </Text>
          {ratioDisplay ? (
            <Text color="colorTextImportant" whiteSpace="nowrap" textAlign="right">
              {ratioDisplay}
            </Text>
          ) : (
            <Box />
          )}
        </Grid>
      ) : (
        <HStack w="100%" justifyContent="space-between" gap="spacing12">
          <StatusLabel statusColor={statusColor} statusDisplayName={statusDisplayName} />
          {ratioDisplay && (
            <Text color="colorTextImportant" whiteSpace="nowrap">
              {ratioDisplay}
            </Text>
          )}
        </HStack>
      )}
    </CellWrapper>
  );

  if (!hasCollateralStatusDetails(data)) {
    return cellContent;
  }

  return <CollateralStatusPopover row={data}>{cellContent}</CollateralStatusPopover>;
}
